import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  sendBorLink,
  sendRegularBorLink,
  sendRocklakeCreditCardAuthorizationLink,
  sendRocklakePaymentAuthorizationLink,
} from '../../../../models/bor'

export type EnvelopeKind = 'canal' | 'regular' | 'rocklake_payment' | 'rocklake_credit_card'

const COPY: Record<EnvelopeKind, { title: string; description: string; label: string }> = {
  canal: {
    title: 'Send Canal BOR Link',
    description:
      'Generate a Canal Broker of Record signing link for this customer. Any existing unsigned Canal BOR envelope will be voided.',
    label: 'Canal BOR',
  },
  regular: {
    title: 'Send BOR Link',
    description:
      'Generate a Broker of Record signing link for this customer. Any existing unsigned BOR envelope will be voided.',
    label: 'BOR',
  },
  rocklake_payment: {
    title: 'Send Rocklake Payment Authorization Link',
    description:
      'Generate a Rocklake Payment Authorization (ACH/EFT direct-debit) signing link for this customer. Any existing unsigned envelope of this type will be voided.',
    label: 'Rocklake Payment Authorization',
  },
  rocklake_credit_card: {
    title: 'Send Rocklake Credit Card Authorization Link',
    description:
      'Generate a Rocklake Credit Card Authorization signing link for this customer. Any existing unsigned envelope of this type will be voided.',
    label: 'Rocklake Credit Card Authorization',
  },
}

const SEND_FN: Record<EnvelopeKind, typeof sendBorLink> = {
  canal: sendBorLink,
  regular: sendRegularBorLink,
  rocklake_payment: sendRocklakePaymentAuthorizationLink,
  rocklake_credit_card: sendRocklakeCreditCardAuthorizationLink,
}

interface SendBorDialogProps {
  orgId: string
  kind: EnvelopeKind
  onSuccess: () => void
  children?: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function SendBorDialog({
  orgId,
  kind,
  onSuccess,
  children,
  open: controlledOpen,
  onOpenChange,
}: SendBorDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = isControlled ? (onOpenChange ?? (() => {})) : setInternalOpen
  const [loadingAction, setLoadingAction] = useState<'send' | 'copy' | null>(null)
  const copy = COPY[kind]
  const send = SEND_FN[kind]

  const handle = async (notifyCustomer: boolean) => {
    const action = notifyCustomer ? 'send' : 'copy'
    setLoadingAction(action)
    try {
      const { link, sentTo } = await send(orgId, notifyCustomer)
      if (notifyCustomer) {
        toast.success(`${copy.label} link sent to ${sentTo}`)
      } else {
        await navigator.clipboard.writeText(link)
        toast.success(`${copy.label} link copied to clipboard`)
      }
      onSuccess()
      setOpen(false)
    } catch {
      toast.error(`Failed to generate ${copy.label} link. Please try again.`)
    } finally {
      setLoadingAction(null)
    }
  }

  const loading = loadingAction !== null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => handle(false)}
            disabled={loading}
            loading={loadingAction === 'copy' ? 'Copying...' : undefined}
          >
            Copy Link
          </Button>
          <Button
            onClick={() => handle(true)}
            disabled={loading}
            loading={loadingAction === 'send' ? 'Sending...' : undefined}
          >
            Send to Customer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
